import { useEffect, useRef, useState } from 'react';
import { ComposerPrimitive, ThreadPrimitive } from '@assistant-ui/react';

import { useApiServices } from '../../api/services';
import { useAgentThread } from '../../hooks/useAgentSidebar';
import { useAgentProgress } from './progress-store';
import { useRunningThreadId } from './agent-runtime';

const GLYPHS = ['✳', '✻', '✽', '✻'];
const TICK_MS = 400;

function formatTokens(total: number): string {
  if (total < 1000) return String(total);
  return `${(total / 1000).toFixed(1)}k`;
}

export function AgentWorking({ tokens }: { tokens: number }) {
  return (
    <ThreadPrimitive.If running>
      <WorkingLine tokens={tokens} />
    </ThreadPrimitive.If>
  );
}

function WorkingLine({ tokens }: { tokens: number }) {
  const { activity, log, answering } = useAgentProgress();
  const { agent } = useApiServices();
  const { threadId } = useAgentThread();
  const runningThreadId = useRunningThreadId() ?? threadId;
  const [frame, setFrame] = useState(0);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    const id = setInterval(() => setFrame((value) => value + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const seconds = Math.round((Date.now() - startedAt.current) / 1000);
  const label = activity ?? (answering ? 'Writing the answer' : 'Working');

  return (
    <div className="border-t border-border/50 px-3 py-2 text-xs" aria-busy="true">
      <p className="flex items-center gap-1.5 font-medium text-foreground">
        <span aria-hidden="true" className="text-primary">
          {GLYPHS[frame % GLYPHS.length]}
        </span>
        <span aria-hidden="true" className="truncate">
          {label}…
        </span>
        <span role="status" className="sr-only">
          {label}
        </span>
      </p>

      <p className="mt-0.5 pl-5 text-muted-foreground">
        <span aria-hidden="true">
          ({seconds}s{tokens > 0 && <> · ↑ {formatTokens(tokens)} tokens</>} ·{' '}
        </span>

        <ComposerPrimitive.Cancel asChild>
          <button
            type="button"
            className="underline hover:text-foreground"
            onClick={() => {
              if (runningThreadId) void agent.cancelRun(runningThreadId).catch(() => undefined);
            }}
          >
            stop
          </button>
        </ComposerPrimitive.Cancel>
        <span aria-hidden="true">)</span>
      </p>

      {log.length > 0 && (
        <ul aria-hidden="true" className="mt-1.5 space-y-0.5">
          {log.map((entry, index) => (
            <li key={`${entry.resource}-${index}`}>
              <span className="text-muted-foreground">⏺ </span>
              <span className="font-medium">{entry.resource}</span>
              <br />
              <span className={entry.failed ? 'text-destructive' : 'text-muted-foreground'}>
                {'  ⎿ '}
                {entry.detail}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
